import { CLIMBING_STYLES } from '../constants.js';
import { SPORT_CLEAN_ASCENT_TYPES, BOULDER_CLEAN_ASCENT_TYPES } from '../gradeUtils.js';

export const calculateCragStats = (data, climbingStyle) => {
  const cragMap = data.reduce((acc, ascent) => {
    if (!ascent['Crag Name'] || !ascent['Route Grade']) return acc;
    
    if (!acc[ascent['Crag Name']]) {
      acc[ascent['Crag Name']] = {
        name: ascent['Crag Name'],
        ascents: [],
        attempts: [], // Store all attempts
        distinctRoutes: new Set(),
        gradeCategories: {
          beginner: new Set(),
          intermediate: new Set(),
          experienced: new Set(),
          expert: new Set(),
          elite: new Set()
        },
        ascentCategories: {
          beginner: 0,
          intermediate: 0,
          experienced: 0,
          expert: 0,
          elite: 0
        },
        // Linear-domain CPG sums per difficulty band (sum of 2^ewbanks or e^vgrade)
        cpgSumsByCategory: {
          beginner: 0,
          intermediate: 0,
          experienced: 0,
          expert: 0,
          elite: 0
        },
        // Initialize ascent type counters
        ascentTypes: {
          onsight: 0,
          flash: 0,
          redpoint: 0,
          pinkpoint: 0,
          send: 0,
          repeat: 0,
          other: 0
        }
      };
    }
    
    // Add to attempts array
    acc[ascent['Crag Name']].attempts.push(ascent);
    
    // Count ascent types
    const ascentType = ascent["Ascent Type"];
    if (climbingStyle === CLIMBING_STYLES.BOULDER) {
      if (ascentType === "Onsight") acc[ascent['Crag Name']].ascentTypes.onsight++;
      else if (ascentType === "Flash") acc[ascent['Crag Name']].ascentTypes.flash++;
      else if (ascentType === "Send") acc[ascent['Crag Name']].ascentTypes.send++;
      else if (ascentType === "Repeat") acc[ascent['Crag Name']].ascentTypes.repeat++;
      else acc[ascent['Crag Name']].ascentTypes.other++;
    } else {
      if (ascentType === "Onsight") acc[ascent['Crag Name']].ascentTypes.onsight++;
      else if (ascentType === "Flash") acc[ascent['Crag Name']].ascentTypes.flash++;
      else if (ascentType === "Red point") acc[ascent['Crag Name']].ascentTypes.redpoint++;
      else if (ascentType === "Pink point") acc[ascent['Crag Name']].ascentTypes.pinkpoint++;
      else acc[ascent['Crag Name']].ascentTypes.other++;
    }

    const isCleanAscent = climbingStyle === CLIMBING_STYLES.BOULDER ? 
      BOULDER_CLEAN_ASCENT_TYPES.includes(ascentType) :
      SPORT_CLEAN_ASCENT_TYPES.includes(ascentType);
    
    if (isCleanAscent && ascent.isClean) {
      acc[ascent['Crag Name']].ascents.push(ascent);
      
      // Categorize by grade based on climbing style
      const grade = climbingStyle === CLIMBING_STYLES.BOULDER ? 
        ascent.vgrade : 
        ascent.ewbanks;
      const routeId = ascent['Route ID'];
      let category;
      
      if (climbingStyle === CLIMBING_STYLES.BOULDER) {
        if (grade < -0.5) category = 'beginner';
        else if (grade <= 0.5) category = 'intermediate';
        else if (grade <= 4) category = 'experienced';
        else if (grade <= 10) category = 'expert';
        else category = 'elite';
      } else {
        if (grade <= 12) category = 'beginner';
        else if (grade <= 18) category = 'intermediate';
        else if (grade <= 24) category = 'experienced';
        else if (grade <= 32) category = 'expert';
        else category = 'elite';
      }
      
      acc[ascent['Crag Name']].gradeCategories[category].add(routeId);
      acc[ascent['Crag Name']].ascentCategories[category]++;

      if (grade !== null && !isNaN(grade)) {
        const linear = climbingStyle === CLIMBING_STYLES.BOULDER
          ? Math.exp(grade)
          : Math.pow(2, grade);
        acc[ascent['Crag Name']].cpgSumsByCategory[category] += linear;
      }
    }
    
    return acc;
  }, {});

  const allCragStats = Object.values(cragMap).map(crag => {
    let cpg;
    if (climbingStyle === CLIMBING_STYLES.BOULDER) {
      const sum = crag.ascents.reduce((acc, ascent) => {
        if (ascent.vgrade === null || isNaN(ascent.vgrade)) return acc;
        return acc + Math.exp(ascent.vgrade);
      }, 0);
      cpg = Math.log(sum);
    } else {
      const sum = crag.ascents.reduce((acc, ascent) => {
        if (ascent.ewbanks === null || isNaN(ascent.ewbanks)) return acc;
        return acc + Math.pow(2, ascent.ewbanks);
      }, 0);
      cpg = Math.log2(sum);
    }

    // Expose per-band linear-domain sums (2^ewbanks or e^vgrade) so the chart
    // can stack them on an exponential x-axis. On a linear x-axis these sum
    // to 2^cpg (or e^cpg for boulder), giving each band a width proportional
    // to its true contribution while CPG differences appear exponentially.
    const sums = crag.cpgSumsByCategory;

    return {
      name: crag.name,
      cpg: Number(cpg.toFixed(2)),
      beginnerCpgLinear: sums.beginner,
      intermediateCpgLinear: sums.intermediate,
      experiencedCpgLinear: sums.experienced,
      expertCpgLinear: sums.expert,
      eliteCpgLinear: sums.elite,
      cleanAscents: crag.ascents.length,
      attempts: crag.attempts.length,
      // Include all ascent type counts
      onsight: crag.ascentTypes.onsight,
      flash: crag.ascentTypes.flash,
      redpoint: crag.ascentTypes.redpoint,
      pinkpoint: crag.ascentTypes.pinkpoint,
      send: crag.ascentTypes.send,
      repeat: crag.ascentTypes.repeat,
      other: crag.ascentTypes.other,
      // Include existing grade breakdowns
      distinctCleanRoutes: crag.distinctRoutes.size,
      beginnerRoutes: crag.gradeCategories.beginner.size,
      intermediateRoutes: crag.gradeCategories.intermediate.size,
      experiencedRoutes: crag.gradeCategories.experienced.size,
      expertRoutes: crag.gradeCategories.expert.size,
      eliteRoutes: crag.gradeCategories.elite.size,
      beginnerAscents: crag.ascentCategories.beginner,
      intermediateAscents: crag.ascentCategories.intermediate,
      experiencedAscents: crag.ascentCategories.experienced,
      expertAscents: crag.ascentCategories.expert,
      eliteAscents: crag.ascentCategories.elite
    };
  });

  return {
    crags: allCragStats,
    minCPG: Math.floor(Math.min(...allCragStats.map(crag => crag.cpg)))
  };
};